<template>
  <div class="repeatable-wrapper panel bg-gray-1 padding-l">
    <div class="flex gap-l flex-break-column flex-middle">
      <form-button
        icon="pencil"
        text="core.form.repeatable.editCta"
        @click.stop.prevent="showModal = true"></form-button>
    </div>

    <modal size="full" :show="showModal" @modal:closed="showModal = false">
      <template #title="{ modal }">
        <h3
          class="modal__title"
          :class="{ 'modal__title--center': modal.centerTitle }">
          {{ translateModalTitle ? tt(editModalTitle) : editModalTitle }}
        </h3>
      </template>
      <template #default>
        <div class="repeatable-wrapper panel bg-gray-1 padding-m l:padding-l">
          <div
            v-for="(item, originalFieldKey) of formConfig.fields"
            :key="uniqueId + '-row-' + originalFieldKey"
            class="panel padding-l margin-l-bottom animate--fast"
            :class="{
              animate__fadeIn:
                addingNewItems && originalFieldKey == value.length - 1,
            }">
            <div class="item">
              <div class="fields-grid margin-inline-end width-expand">
                <div class="item-fields-wrapper width-expand">
                  <h4 class="margin-remove">
                    {{ item.name }} ({{ getFieldDisplayLabel(item) }})
                  </h4>
                  <hr class="max-width-400 margin-remove-inline" />
                  <div class="flex gap-m flex-wrap">
                    <div
                      v-for="(editConfig, key) of editableKeys"
                      class="field-container min-width-300 max-width-600"
                      style="flex: 50%">
                      <div class="flex gap-m">
                        <div
                          class="field-wrapper-inner flex-column gap-m"
                          style="flex: 0 0 310px; max-width: 100%">
                          <form-input
                            class="width-expand"
                            v-model="value.fields[originalFieldKey][key]"
                            :type="editConfig.type"
                            :label="editConfig.label"
                            :legend="
                              getFieldKeyLegend(
                                formConfig.fields[originalFieldKey],
                                key
                              )
                            " />
                          <div
                            class="actions-wrapper flex gap-m width-expand"
                            v-if="key != 'omit'">
                            <form-button
                              icon="clipboard-x"
                              style="
                                flex: 50%;
                                max-width: calc(50% - var(--margin-m) / 2);
                              "
                              theme="inverse"
                              size="s"
                              text="core.form.formConfigInput.setValueToEmptyString"
                              @click="
                                setValueToFalse(originalFieldKey, key)
                              "></form-button>
                            <form-button
                              class="animate__fadeIn animate--fastest"
                              icon="trash"
                              size="s"
                              theme="danger"
                              style="flex: 50%"
                              text="core.form.formConfigInput.clear"
                              v-if="
                                value?.fields?.[originalFieldKey]?.[key] !==
                                  undefined &&
                                value?.fields?.[originalFieldKey]?.[key] !==
                                  null
                              "
                              @click="
                                resetFieldKey(originalFieldKey, key)
                              "></form-button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="field-container min-width-300 max-width-600 margin-l-top"
                    style="flex: 50%">
                    <h4 class="margin-s-bottom">
                      {{ tu("core.form.formConfigInput.validationEditTitle") }}
                    </h4>
                    <div class="flex gap-m flex-middle">
                      <form-input
                        v-if="
                          item.validation && typeof item.validation === 'object'
                        "
                        v-for="validationKey of getValidationKeys(item)"
                        class="width-expand"
                        v-model="
                          value.fields[originalFieldKey].validation[
                            validationKey
                          ].enabled
                        "
                        :type="'onOffToggle'"
                        :label="`validation.${validationKey}ValidationName`" />
                      <span v-else>{{
                        t("core.form.formConfigInput.validationEditUnavailable")
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </modal>
  </div>
</template>

<script>
import RepeatableInputMixin from "@/client/extensions/mixins/baseComponents/form/controls/input/repeatableInput.js";
import _ from "lodash";

export default {
  mixins: [RepeatableInputMixin],
  props: {
    formConfig: {
      type: Object,
      default: () => ({}),
    },
    editableKeys: {
      type: Object,
      default: () => {
        return {
          label: {
            label: "core.form.formConfigInput.editableKeys.label",
            type: "text",
          },
          placeholder: {
            label: "core.form.formConfigInput.editableKeys.placeholder",
            type: "text",
          },
          name: {
            label: "core.form.formConfigInput.editableKeys.name",
            type: "text",
          },
          defaultValue: {
            label: "core.form.formConfigInput.editableKeys.defaultValue",
            type: "text",
          },
          omit: {
            label: "core.form.formConfigInput.editableKeys.omit",
            type: "onOffToggle",
          },
        };
      },
    },
    editModalTitle: {
      type: String,
      default: "core.form.repeatable.editModalTitle",
    },
    translateModalTitle: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {},
  data() {
    return {
      showModal: false,
    };
  },
  computed: {
    value: {
      get() {
        return this.modelValue;
      },
      set(value) {
        let safeVal = this.enforceValueIntegrity(value);
        this.$emit("update:modelValue", safeVal);
      },
    },
    itemCount() {
      if (!this.value || typeof this.value !== "object") {
        return 0;
      }

      return Object.keys(this.value).length;
    },
  },
  created() {
    this.enforceValueIntegrity();
  },
  watch: {},
  methods: {
    getFieldDisplayLabel(item) {
      let base =
        item.label && item.label !== "" ? item.label : item.placeholder;
      return item.autoTranslate !== false ? this.t(base) : base;
    },
    enforceValueIntegrity(inputValue) {
      let hasInputValue = !!inputValue;
      let finalValue = inputValue ?? this.value;
      if (
        typeof finalValue === "undefined" ||
        !finalValue ||
        typeof finalValue !== "object"
      ) {
        finalValue = {};
      }

      if (!finalValue.fields) {
        finalValue.fields = {};
      }

      let targetFields = Object.keys(this?.formConfig?.fields ?? {});

      targetFields.forEach((key) => {
        if (!finalValue.fields[key]) {
          finalValue.fields[key] = {};
        }

        // cast empty strings to undefined as they cause issues
        for (const [fieldKey, fieldValue] of Object.entries(
          finalValue.fields[key]
        )) {
          if (fieldValue === "") {
            finalValue.fields[key][fieldKey] = undefined;
          }
        }

        // enforce presence of the omit key. this allows up to let admin have default disabled fields
        if (typeof finalValue.fields[key].omit === "undefined") {
          let omitDefaultValue = this?.formConfig?.fields?.[key]?.omit;
          finalValue.fields[key].omit = omitDefaultValue || false;
        }

        if (
          !finalValue.fields[key].validation &&
          this?.formConfig?.fields[key].validation
        ) {
          finalValue.fields[key].validation = _.cloneDeep(
            this?.formConfig?.fields[key].validation
          );
          Object.values(finalValue.fields[key].validation).forEach((conf) => {
            if (conf?.enabled !== false) {
              conf.enabled = true;
            }
          });
        }
      });

      // remove validation not present in configuration
      for (const [finalFieldKey, finalFieldValue] of Object.entries(
        finalValue.fields
      )) {
        if (!finalFieldValue.validation) {
          continue;
        }

        Object.keys(finalFieldValue.validation).forEach((validationKey) => {
          // if the config field doesnt have it - delete it
          if (
            !this?.formConfig?.fields[finalFieldKey].validation[validationKey]
          ) {
            delete finalFieldValue.validation[validationKey];
          }
        });
      }
      if (hasInputValue) {
        return finalValue;
      } else {
        this.value = finalValue;
      }
    },
    getFieldKeyValue(originalFieldKey, key) {
      if (!this.value?.fields) {
        return undefined;
      }

      if (!this.value?.fields[originalFieldKey]) {
        return undefined;
      }
      return this.value?.fields[originalFieldKey][key];
    },
    setFieldKeyValue(originalFieldKey, subKey, val) {
      if (!this.value.fields) {
        this.value.fields = {};
      }
      if (!this.value.fields[originalFieldKey]) {
        this.value.fields[originalFieldKey] = {};
      }

      this.value.fields[originalFieldKey][subKey] = val;
      this.value.fields[originalFieldKey].autoTranslate = false;
      if (typeof this.value.fields[originalFieldKey].ommit === "undefined") {
        this.value.fields[originalFieldKey].ommit = false;
      }
    },
    getFieldKeyLegend(item, key) {
      if (!item[key] || item[key] === "") {
        return this.t("core.form.formConfigInput.editKeyOriginalValueNone");
      }
      if (item.autoTranslate !== false) {
        return this.t("core.form.formConfigInput.editKeyLegend", {
          content: this.t(item[key]),
        });
      }

      // no auto translate
      return this.t("core.form.formConfigInput.editKeyLegend", {
        content: item[key],
      });
    },
    resetField(fieldKey) {
      if (!this.value.fields) {
        this.value.fields = {};
      }

      this.value.fields[fieldKey] = {};
    },
    resetFieldKey(fieldKey, key) {
      if (!this.value.fields) {
        this.value.fields = {};
      }

      if (!this.value.fields[fieldKey]) {
        this.value.fields[fieldKey] = {};
      }

      if (this.value.fields[fieldKey].hasOwnProperty(key)) {
        this.value.fields[fieldKey][key] = undefined;
      }
    },
    setValueToFalse(fieldKey, key) {
      if (!this.value.fields) {
        this.value.fields = {};
      }

      if (!this.value.fields[fieldKey]) {
        this.value.fields[fieldKey] = {};
      }

      this.value.fields[fieldKey][key] = false;
    },
    // custom input class implementation
    getInputClass(type) {
      return [];
    },
    getValidationKeys(field) {
      if (!field.validation) {
        return [];
      }
      return Object.keys(field.validation);
    },
  },
};
</script>
